'use client'

import React, { useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import { MaterializeWords } from './reveal'

const HomeIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 11.5 12 4l9 7.5" />
    <path d="M5 10v10h14V10" />
    <path d="M9.5 20v-6h5v6" />
  </svg>
)
const KeyIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <circle cx="8" cy="15" r="5" />
    <path d="m11.5 11.5 8.5-8.5" />
    <path d="m17 5 2.5 2.5" />
    <path d="m14 8 2.5 2.5" />
  </svg>
)


// Marks an intro "done" after `totalMs`, OR immediately when the user taps/clicks anywhere to skip.
// A skip-tap reveals only: it swallows that same tap's click so it can't activate a button/card the
// user hadn't seen yet (pointerdown reveals → the same tap's click would otherwise hit the now-live
// element). Runs once per activation; later genuine taps are untouched.
function useIntroFinish(active: boolean, totalMs: number, setDone: (v: boolean) => void) {
  useEffect(() => {
    if (!active) return
    let finished = false
    const finish = (skipTap: boolean) => {
      if (finished) return
      finished = true
      setDone(true)
      window.removeEventListener('pointerdown', onPointerDown)
      if (skipTap) {
        let t: ReturnType<typeof setTimeout>
        const swallow = (e: Event) => {
          e.preventDefault()
          e.stopPropagation()
          window.removeEventListener('click', swallow, true)
          clearTimeout(t)
        }
        window.addEventListener('click', swallow, true)
        t = setTimeout(() => window.removeEventListener('click', swallow, true), 700)
      }
    }
    // Require two taps within ~3s to skip — a single tap is too easy to trigger by accident.
    let firstTapAt = 0
    const onPointerDown = () => {
      const now = Date.now()
      if (now - firstTapAt <= 3000) finish(true)
      else firstTapAt = now
    }
    const timer = setTimeout(() => finish(false), totalMs)
    window.addEventListener('pointerdown', onPointerDown)
    return () => {
      clearTimeout(timer)
      window.removeEventListener('pointerdown', onPointerDown)
    }
  }, [active, totalMs, setDone])
}

// Two-slide "presentation deck" landing: hero → (CTA) → audience chooser.
export function LandingDeck({ name, heroImg }: { name: string; heroImg?: string }) {
  const [slide, setSlide] = useState(0)
  // Hold the hero entrance until the background image has loaded (so it doesn't fly in over
  // a blank frame). If there's no hero image, start immediately.
  const [ready, setReady] = useState(false)
  // Each intro finished (or skipped) — gates interactivity so invisible buttons/cards can't be
  // tapped before they appear.
  const [heroDone, setHeroDone] = useState(false)
  const [chooserDone, setChooserDone] = useState(false)
  const bgRef = useRef<HTMLImageElement>(null)
  useEffect(() => {
    if (!heroImg || bgRef.current?.complete) setReady(true)
  }, [heroImg])
  // Hero intro ~16.6s; chooser intro ~11.6s (begins when the chooser slide becomes active).
  useIntroFinish(ready && slide === 0, 16700, setHeroDone)
  useIntroFinish(slide === 1, 11800, setChooserDone)

  return (
    <div className="deck">
      <div className="deck__track" style={{ transform: `translateX(-${slide * 100}%)` }}>
        {/* Slide 1 — hero */}
        <section className={`deck__slide deck__hero${slide === 0 ? ' is-active' : ''}`} aria-hidden={slide !== 0}>
          {heroImg ? (
            <img ref={bgRef} className="deck__bg" src={heroImg} alt="" onLoad={() => setReady(true)} />
          ) : null}
          <div className="deck__overlay" />
          <div className="deck__content">
            <div className={`hero-settle ${ready ? 'play' : 'prep'}${heroDone ? ' done' : ''}`}>
              <div className="deck__col">
              <span className="eyebrow">Welcome to {name}</span>
              <h1>
                <MaterializeWords text="Everything you need to settle into your new home." base={2.7} cps={0.085} />
              </h1>
              <p className="lead">
                <MaterializeWords
                  phrases={[
                    'Your guided starting point',
                    '— a move-in checklist and the B.C. resources that matter,',
                    'tailored to you.',
                  ]}
                  base={8.3}
                  cps={0.04}
                  pause={0.8}
                  emphasize={[2]}
                />
              </p>
              <div className="hero-actions">
                <button className={`btn btn-cta hero-drift${ready ? ' lit' : ''}`} onClick={() => setSlide(1)}>
                  <svg className="cta-edge" aria-hidden="true">
                    <rect className="cta-edge__r" x="0" y="0" width="100%" height="100%" rx="10" ry="10" pathLength="100" />
                  </svg>
                  <span className="cta-label">Let&apos;s get started →</span>
                </button>
              </div>
              <div className="hero-powered">
                Powered by <strong>B.C. Strata Tech</strong>
              </div>
              </div>
            </div>
          </div>
        </section>

        {/* Slide 2 — chooser */}
        <section
          className={`deck__slide deck__chooser${slide === 1 ? ' is-active' : ''}${chooserDone ? ' is-done' : ''}`}
          aria-hidden={slide !== 1}
        >
          <div className="deck__content">
            <div className="container">
              <button className="deck__back" onClick={() => setSlide(0)}>
                ← Back
              </button>
              <div className="section-head" style={{ textAlign: 'center', maxWidth: '46rem', margin: '0 auto 2.25rem' }}>
                <span className="eyebrow">Let&apos;s get you to the right place</span>
                <h2 className="chooser-h2">
                  <span className="chooser-type">
                    <MaterializeWords
                      phrases={['First', '— are you a homeowner or a renter?']}
                      base={3.2}
                      cps={0.05}
                      pause={0.7}
                    />
                  </span>
                </h2>
                <p className="lead">
                  <MaterializeWords
                    phrases={['Pick one', "and we'll tailor your checklist and resources."]}
                    base={7.0}
                    cps={0.038}
                    pause={0.6}
                  />
                </p>
              </div>
              <div className="chooser">
                <Link className="chooser-card" href="/owner">
                  <span className="chooser-icon" style={{ background: 'var(--brand-primary)' }}>
                    <HomeIcon />
                  </span>
                  <h3>I&apos;m a homeowner</h3>
                  <p>You own — or just bought — a home here.</p>
                  <span className="chooser-go">Owner resource centre →</span>
                </Link>
                <Link className="chooser-card" href="/renters">
                  <span className="chooser-icon" style={{ background: 'var(--brand-accent)' }}>
                    <KeyIcon />
                  </span>
                  <h3>I&apos;m a renter</h3>
                  <p>You&apos;re renting a home here.</p>
                  <span className="chooser-go">Renter resource centre →</span>
                </Link>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  )
}
